import React, { useState } from 'react';
import { ChevronDown, Truck, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ProductDescription({ product }) {
  const [openSections, setOpenSections] = useState({
    description: true,
    shipping: false,
    returns: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Function to render HTML content safely
  const renderHTMLContent = (htmlContent, fallbackText) => {
    if (htmlContent) {
      return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }
    return <p className="leading-relaxed">{fallbackText}</p>;
  };

  const sections = [
    {
      id: 'description',
      title: 'Description',
      icon: null,
      content: (
        <div className="text-sm text-gray-600 space-y-3 text-md">
          <p className="text-sm text-gray-400">Reference: {product?.reference || 'N/A'}</p>
         
          {product?.description ? (
            <div dangerouslySetInnerHTML={{ __html: product.description }} />
          ) : product?.composition && product.composition.length > 0 ? (
            <div>
              <p className="font-medium mb-2 uppercase tracking-wide">COMPOSITION</p>
              <div className="space-y-1">
                {product.composition.map((item, index) => (
                  <p key={index}>- {item}</p>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )
    },
    {
      id: 'shipping',
      title: 'Shipping',
      icon: <Truck className="w-4 h-4" />,
      content: (
        <div className="text-sm text-gray-600 space-y-3">
          {product?.shippingHTML ? (
            <div dangerouslySetInnerHTML={{ __html: product.shippingHTML }} />
          ) : (
            <>
              <div>
                <p className="font-medium mb-2 uppercase tracking-wide">DELIVERY OPTIONS</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Standard Shipping</span>
                    <span className="font-medium">5-7 business days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Express Shipping</span>
                    <span className="font-medium">2-3 business days</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Next Day Delivery</span>
                    <span className="font-medium">1 business day</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-medium mb-2 uppercase tracking-wide">SHIPPING COSTS</p>
                <div className="space-y-1">
                
                  <p>• Standard shipping: &#8377; 60</p>
                  <p>• Express shipping: &#8377; 90</p>
                
                </div>
              </div>
              <p className="text-sm text-gray-500 italic">
                Orders placed before 2 PM EST ship the same day (Monday-Friday)
              </p>
            </>
          )}
        </div>
      )
    },
    {
      id: 'returns',
      title: 'Returns & Refunds',
      icon: <RotateCcw className="w-4 h-4" />,
      content: (
        <div className="text-sm text-gray-600 space-y-3">
          {product?.returnsHTML ? (
            <div dangerouslySetInnerHTML={{ __html: product.returnsHTML }} />
          ) : (
            <>
              <div>
                <p className="font-medium mb-2 uppercase tracking-wide">RETURN POLICY</p>
                <div className="space-y-1">
                  <p>• 30-day return window from delivery date</p>
                  <p>• Items must be unused and in original packaging</p>
                  <p>• Original tags and labels must be attached</p>
                  <p>• Free returns for defective or damaged items</p>
                </div>
              </div>
              <div>
                <p className="font-medium mb-2 uppercase tracking-wide">REFUND PROCESS</p>
                <div className="space-y-1">
                  <p>• Refunds processed within 3-5 business days</p>
                  <p>• Original payment method will be credited</p>
                  <p>• Shipping costs are non-refundable (except for our errors)</p>
                  <p>• Return shipping label provided for eligible returns</p>
                </div>
              </div>
              <div>
                <p className="font-medium mb-2 uppercase tracking-wide">HOW TO RETURN</p>
                <div className="space-y-1">
                  <p>1. Contact customer service to initiate return</p>
                  <p>2. Print the prepaid return label</p>
                  <p>3. Package items securely in original packaging</p>
                  <p>4. Drop off at any authorized shipping location</p>
                </div>
              </div>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="border-t pt-4 space-y-4">
      {sections.map((section) => (
        <div key={section.id} className="border-b border-gray-100 last:border-b-0 pb-4 last:pb-0">
          <button
            onClick={() => toggleSection(section.id)}
            className="flex items-center justify-between w-full text-left text-sm mb-3 hover:text-gray-700 transition-colors duration-200"
          >
            <div className="flex items-center gap-2">
              {section.icon}
              <span className="font-medium">{section.title}</span>
            </div>
            <motion.div
              animate={{ rotate: openSections[section.id] ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ChevronDown className="w-4 h-4" />
            </motion.div>
          </button>
          
          <AnimatePresence>
            {openSections[section.id] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ 
                  duration: 0.3, 
                  ease: "easeInOut",
                  opacity: { duration: 0.2 }
                }}
                className="overflow-hidden text-left text-md text-[#111]"
              >
                {section.content}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

